import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { getErrorMessage } from "src/views/remote-resources/modals/CreateResourceModal";
import RecentChats from "src/views/assistant/components/splashScreen/RecentChats";
import { useProposalGenerationStore } from "../store/ProposalGenerationStore";
import { useActiveDatasetIds } from "src/views/assistant/stores/ChatStore";
import {
  getReportTypeDisplayName,
  stageDisplayConfig,
} from "../services/ProposalGenerationServices";
import { useGetStageConfig } from "../services/ProposalGenerationConfigService";
import { StageConfigButton } from "./StageConfigButton";
import TemplateSelector from "./TemplateSelector.tsx";
import TemplateCreator from "./TemplateCreator.tsx";
import InputDocumentsUploader from "src/views/products/workflow/components/InputDocumentsUploader";
import { spaNavigate } from "src/views/products/workflow/routeLink";
import WorkflowSplashChatInput from "src/views/products/workflow/components/WorkflowSplashChatInput";
import WorkflowPersonaSlotsPanel from "src/views/products/workflow/components/WorkflowPersonaSlotsPanel";
import { NucToast } from "src/reusable/components/Toast";
import { partitionBlockingInputFiles } from "src/views/products/workflow/store/inputFileReadiness";
import { useIsPermissionDenied } from "src/helpers/Auth";
import { PROPOSAL_GENERATION_PRODUCT } from "../proposalGenerationProduct";
import RFPViewer from "./RFPViewer.tsx";
import ReferenceDatasetSelector from "./ReferenceDatasetSelector.tsx";
import { PageTopHelpButton } from "src/components/help";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
import ArticleIcon from "@mui/icons-material/Article";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

type ProposalGenerationSplashScreenProps = {
  chatStreamError: string;
  isChatConnectionError: boolean;
  isChatConnected: boolean;
  isChatLoading: boolean;
  isChatEnded: boolean;
  terminateConnection: () => void;
  onGenerateReport: (userMessage: string) => Promise<boolean>;
};

const ProposalGenerationSplashScreen = ({
  chatStreamError,
  isChatConnectionError,
  isChatLoading,
  onGenerateReport,
}: ProposalGenerationSplashScreenProps) => {
  const {
    inputFiles,
    selectedTemplate,
    reportType,
    sequenceNumber,
    stageReportPrefix,
    stageReportSuffixLabel,
  } = useProposalGenerationStore();
  const history = useHistory();
  const isGenerateReportDenied = useIsPermissionDenied("project:write");
  const { data: stageConfig } = useGetStageConfig();
  const activeDatasetIds = useActiveDatasetIds();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [noDatasetWarningShown, setNoDatasetWarningShown] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Get dynamic report type display name. Prefer the run's snapshotted
  // prefix/suffix (set on the store from the stages query) over the live config
  // so a configured prefix renders as "<Stage Name> <Suffix>", not the raw
  // title-cased report_type.
  const reportTypeDisplayName = getReportTypeDisplayName(
    reportType || undefined,
    sequenceNumber || undefined,
    stageDisplayConfig(
      stageReportPrefix,
      stageReportSuffixLabel,
      stageConfig?.effective,
    ),
  );

  // Files are ready if all uploaded/selected files have completed processing AND
  // have their artifact ID available (required for report generation)
  const areFilesReady =
    inputFiles.length > 0 &&
    inputFiles.every(
      (rfpFile) =>
        rfpFile.uploadStatus === "uploaded" &&
        rfpFile.processingStatus === "completed" &&
        !!rfpFile.id,
    );

  const handleGenerateSubmit = async (text: string): Promise<boolean> => {
    // Same readiness the Generate button used to enforce via disabled state;
    // a chat box can't be disabled without hiding WHY, so send explains.
    if (isGenerateReportDenied) {
      NucToast({
        message: "User doesn't have permissions project:write",
        type: "error",
        hideProgressBar: false,
      });
      return false;
    }
    // A failed upload never becomes ready, so it must not be reported as
    // still processing: that message would never come true.
    const { failed } = partitionBlockingInputFiles(inputFiles);
    if (failed.length > 0) {
      NucToast({
        message: `Upload failed for ${failed
          .map((file) => file.name)
          .join(", ")}. Remove the failed document(s) to continue.`,
        type: "error",
        hideProgressBar: false,
      });
      return false;
    }
    if (!areFilesReady) {
      NucToast({
        message:
          "Upload input documents and wait for processing to complete first.",
        type: "error",
        hideProgressBar: false,
      });
      return false;
    }
    if (!selectedTemplate) {
      NucToast({
        message: "Please select a template first.",
        type: "error",
        hideProgressBar: false,
      });
      return false;
    }

    // Two-step no-dataset guard, same as the button's two-click warning:
    // first send warns, sending again proceeds.
    if (activeDatasetIds.length === 0 && !noDatasetWarningShown) {
      setSnackbarOpen(true);
      setNoDatasetWarningShown(true);
      return false;
    }

    setIsGenerating(true);
    try {
      const success = await onGenerateReport(text);
      if (success) {
        setNoDatasetWarningShown(false);
        setSnackbarOpen(false);
      }
      return success;
    } finally {
      setIsGenerating(false);
    }
  };

  // Reset warning state when user adds datasets
  useEffect(() => {
    if (activeDatasetIds.length > 0 && noDatasetWarningShown) {
      setNoDatasetWarningShown(false);
      setSnackbarOpen(false);
    }
  }, [activeDatasetIds.length, noDatasetWarningShown]);

  return (
    <Box
      sx={{
        height: "inherit",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: 3,
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          justifyContent: "center",
          alignSelf: "center",
          width: "100%",
          mb: 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 0.5,
            width: "100%",
            mb: 1,
          }}
        >
          <Button
            // href + onClick, not component={RouterLink}: MUI's polymorphism
            // does not typecheck against the nested @types/react that
            // @types/react-router pulls in.
            href={`${PROPOSAL_GENERATION_PRODUCT.basePath}/runs`}
            onClick={spaNavigate(() =>
              history.push(`${PROPOSAL_GENERATION_PRODUCT.basePath}/runs`),
            )}
            size="small"
            startIcon={<ListAltOutlinedIcon />}
            sx={{ mr: "auto", textTransform: "none" }}
            data-testid="propgen-btn-runs"
          >
            Runs
          </Button>
          <WorkflowPersonaSlotsPanel product={PROPOSAL_GENERATION_PRODUCT} />
          <StageConfigButton />
          <PageTopHelpButton
            productLabel="Proposal Generation"
            product="proposal-generation"
          />
        </Box>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent="center"
          sx={{ width: "100%", mb: 2 }}
          flexWrap="wrap"
        >
          <img
            src="/images/nuclearn-n-full.png"
            alt="Nuclearn Logo"
            width={40}
            height={40}
          />
          <Typography
            component="h1"
            data-testid="propgen-page-heading"
            sx={{ fontFamily: "Poppins", fontSize: "36px" }}
          >
            Proposal Generation
          </Typography>
        </Stack>
        {sequenceNumber != null && sequenceNumber > 1 && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              bgcolor: "primary.main",
              color: "primary.contrastText",
              borderRadius: 2,
              px: 2,
              py: 0.5,
              mb: 1.5,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600}>
              Stage {sequenceNumber}: {reportTypeDisplayName}
            </Typography>
          </Box>
        )}
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {sequenceNumber != null && sequenceNumber > 1
            ? `Configure input documents, datasets, and template for this ${reportTypeDisplayName.toLowerCase()}`
            : `Upload an RFP and select a template to generate a ${reportTypeDisplayName.toLowerCase()}`}
        </Typography>
      </Box>

      {isChatConnectionError && (
        <Box sx={{ width: "100%", maxWidth: 900, mb: 2 }}>
          <Alert
            severity="error"
            data-testid="propgen-error-banner"
            sx={{ mb: 2, width: "100%" }}
          >
            <Typography variant="body2">
              {getErrorMessage(chatStreamError) ||
                "An error occurred while connecting to the chat."}
            </Typography>
          </Alert>
        </Box>
      )}

      <Box sx={{ width: "100%", maxWidth: 800, mb: 4 }}>
        <Stack spacing={3}>
          {/* Step 1: Reference Datasets */}
          <Card elevation={2}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: "0.875rem",
                    bgcolor: "primary.main",
                  }}
                >
                  1
                </Avatar>
                <StorageOutlinedIcon color="primary" />
                <Typography variant="h6" component="h2">
                  Reference Datasets
                </Typography>
                <Tooltip title="Select existing datasets to search through when generating your report. The AI will use documents from these datasets as reference material.">
                  <IconButton
                    size="small"
                    aria-label="Reference datasets info"
                    sx={{ p: 0.25 }}
                  >
                    <InfoOutlinedIcon
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select existing datasets to search through as reference material
                when generating your report.
              </Typography>
              <ReferenceDatasetSelector />
            </CardContent>
          </Card>

          {/* Step 2: Input Documents */}
          <Card elevation={2}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: "0.875rem",
                    bgcolor: "primary.main",
                  }}
                >
                  2
                </Avatar>
                <DescriptionIcon color="primary" />
                <Typography variant="h6" component="h2">
                  Input Documents
                </Typography>
                <Tooltip title="Select specific documents or upload files to use as input. These should include the RFP and any related materials that contain the requirements and information the AI should use in your generated report.">
                  <IconButton
                    size="small"
                    aria-label="Input documents info"
                    sx={{ p: 0.25 }}
                  >
                    <InfoOutlinedIcon
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select or upload input documents (RFPs, requirements,
                specifications) that the AI should use in your generated report.
              </Typography>
              <InputDocumentsUploader
                store={useProposalGenerationStore}
                productKey={PROPOSAL_GENERATION_PRODUCT.productKey}
                testIdPrefix={PROPOSAL_GENERATION_PRODUCT.testIdPrefix}
                analyticsPrefix="proposal-gen"
                uploadTooltip="Upload documents such as RFPs, requirements, or specifications that will be used as direct input for generating your report."
              />
            </CardContent>
          </Card>

          {/* Step 3: Output Template */}
          <Card elevation={2}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: "0.875rem",
                    bgcolor: "primary.main",
                  }}
                >
                  3
                </Avatar>
                <ArticleIcon color="primary" />
                <Typography variant="h6" component="h2">
                  {isCreatingTemplate
                    ? "Create New Template"
                    : "Output Template"}
                </Typography>
                {!isCreatingTemplate && (
                  <Tooltip title="Choose a template that defines the structure and format of your generated report. You can also create custom templates.">
                    <IconButton
                      size="small"
                      aria-label="Report template info"
                      sx={{ p: 0.25 }}
                    >
                      <InfoOutlinedIcon
                        sx={{ fontSize: 18, color: "text.secondary" }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {isCreatingTemplate
                  ? "Upload a template file and provide details"
                  : `Select or create a template for the generated ${reportTypeDisplayName.toLowerCase()}`}
              </Typography>
              {isCreatingTemplate ? (
                <TemplateCreator
                  onCancel={() => setIsCreatingTemplate(false)}
                  onSuccess={() => setIsCreatingTemplate(false)}
                />
              ) : (
                <TemplateSelector
                  onCreateNew={() => setIsCreatingTemplate(true)}
                />
              )}
            </CardContent>
          </Card>

          {/* Selected Documents and Datasets Viewer */}
          <RFPViewer />

          {/* Kickoff chat box — prefilled with the generate instruction,
              editable, re-syncing to the report type until the user types. */}
          <Box sx={{ pt: 1 }} data-testid="propgen-btn-generate">
            <WorkflowSplashChatInput
              defaultValue={`Generate a ${reportTypeDisplayName}`}
              sendUserMessage={handleGenerateSubmit}
              isChatLoading={isChatLoading || isGenerating}
              placeholder={`Describe what to generate — defaults to a ${reportTypeDisplayName.toLowerCase()}`}
              product={PROPOSAL_GENERATION_PRODUCT.productKey}
            />
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <RecentChats productKey="proposal-generation" />
      </Box>

      {/* Dataset Warning Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={10000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          variant="filled"
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => setSnackbarOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{ minWidth: { xs: "auto", sm: 400 } }}
        >
          <Typography variant="body2">
            No datasets selected. Generating without datasets may produce less
            accurate results. Send again to continue.
          </Typography>
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProposalGenerationSplashScreen;
